import type { BureauAlarm } from '../services/bureauManagement/bureauManagement.types';
import { formatDateTime } from './formatDateTime';

/**
 * Escape a string for safe HTML embedding.
 */
export function escapeHtml(str: string): string {
  return String(str)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}

/**
 * Normalise the recipient display name, replacing "Bureau"-only values
 * (e.g. "Bureau", "Bureau Bureau") with "Connected Services Hub".
 */
export function formatAlarmRecipient(alarm: BureauAlarm): string {
  const raw = alarm.bureauRecipientName || alarm.bureauName || alarm.bureauEndpoint || '';
  return /^(Bureau\s*)+$/i.test(raw.trim()) ? 'Connected Services Hub' : raw;
}

/**
 * Build the canonical ordered list of label/value pairs for an alarm.
 * Pass `acknowledgedByName` when the display name is known (single-alarm views).
 */
export function getAlarmSections(
  alarm: BureauAlarm,
  acknowledgedByName?: string | null,
): Array<[string, string]> {
  return [
    ['Site Name', alarm.siteName || alarm.siteIdentifier || ''],
    ['Site Identifier', alarm.siteIdentifier || ''],
    ['Recipient', formatAlarmRecipient(alarm)],
    ['Path', alarm.pathToAlarm || alarm.path || alarm.alarmIdentifier || ''],
    ['Priority', alarm.priority !== null && alarm.priority !== undefined ? String(alarm.priority) : 'Not Set'],
    ['Last Occurrence', formatDateTime(alarm.lastOccurrenceAt)],
    ['Acknowledged', alarm.localAck ? 'Yes' : 'No'],
    ...(alarm.localAck && acknowledgedByName ? [['Acknowledged By', acknowledgedByName] as [string, string]] : []),
    ['Alarm State', alarm.sourceAlarmState || ''],
    ['Alarm Message', alarm.alarmMessage || ''],
  ];
}

/**
 * Convert sections to a plain-text block (e.g. for clipboard plain/text or mailto body).
 */
export function alarmSectionsToText(sections: Array<[string, string]>): string {
  return sections.map(([h, v]) => `${h}\n${v}`).join('\n\n');
}

/**
 * Convert sections to an HTML table suitable for rich clipboard paste or email.
 */
export function alarmSectionsToHtml(sections: Array<[string, string]>): string {
  const rows = sections
    .map(
      ([h, v]) =>
        `<tr><th style="text-align:left;padding:4px 8px;border:1px solid #ddd;background:#f6f6f6">${escapeHtml(h)}</th>` +
        `<td style="padding:4px 8px;border:1px solid #ddd">${escapeHtml(v)}</td></tr>`,
    )
    .join('');
  return `<table style="border-collapse:collapse;margin:8px 0">${rows}</table>`;
}
